import { Badge } from '../Badge';
import type { ActiveFilter, DataGridFilter } from './types';

interface DateRangeValue {
  from?: string;
  to?: string;
}

export interface DataGridFiltersProps {
  filters: DataGridFilter[];
  activeFilters: ActiveFilter[];
  onFilterChange: (filters: ActiveFilter[]) => void;
}

function isDateRangeValue(value: unknown): value is DateRangeValue {
  if (!value || typeof value !== 'object') return false;
  const maybeRange = value as Record<string, unknown>;
  return typeof maybeRange.from === 'string' || typeof maybeRange.to === 'string';
}

function isActiveValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (isDateRangeValue(value)) return Boolean(value.from || value.to);
  return true;
}

function getFilterValue(activeFilters: ActiveFilter[], id: string): unknown {
  return activeFilters.find((filter) => filter.id === id)?.value;
}

function upsertFilter(activeFilters: ActiveFilter[], id: string, value: unknown): ActiveFilter[] {
  const nextFilters = activeFilters.filter((filter) => filter.id !== id);
  if (!isActiveValue(value)) return nextFilters;
  return [...nextFilters, { id, value }];
}

export function DataGridFilters({ filters, activeFilters, onFilterChange }: DataGridFiltersProps) {
  const activeCount = activeFilters.filter((filter) => isActiveValue(filter.value)).length;

  return (
    <div className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Filters</h3>
          <Badge variant="neutral" size="sm">
            {activeCount}
          </Badge>
        </div>
        <button
          type="button"
          onClick={() => onFilterChange([])}
          className="text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none rounded px-1"
        >
          Clear filters
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filters.map((filter) => {
          const value = getFilterValue(activeFilters, filter.id);

          if (filter.type === 'text') {
            return (
              <label key={filter.id} className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{filter.label}</span>
                <input
                  type="text"
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) =>
                    onFilterChange(upsertFilter(activeFilters, filter.id, event.target.value.trim()))
                  }
                  placeholder={filter.placeholder ?? `Filter by ${filter.label}`}
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
              </label>
            );
          }

          if (filter.type === 'select') {
            return (
              <label key={filter.id} className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{filter.label}</span>
                <select
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => onFilterChange(upsertFilter(activeFilters, filter.id, event.target.value))}
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                >
                  <option value="">{filter.placeholder ?? 'All'}</option>
                  {(filter.options ?? []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            );
          }

          if (filter.type === 'multi-select') {
            const selectedValues = Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

            return (
              <div key={filter.id} className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{filter.label}</span>
                <div className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 max-h-40 overflow-auto space-y-1">
                  {(filter.options ?? []).map((option) => {
                    const checked = selectedValues.includes(option.value);
                    return (
                      <label key={option.value} className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => {
                            const nextSelected = event.target.checked
                              ? [...selectedValues, option.value]
                              : selectedValues.filter((selected) => selected !== option.value);
                            onFilterChange(upsertFilter(activeFilters, filter.id, nextSelected));
                          }}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                        />
                        {option.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          }

          const rangeValue = isDateRangeValue(value) ? value : {};

          return (
            <div key={filter.id} className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{filter.label}</span>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={rangeValue.from ?? ''}
                  onChange={(event) =>
                    onFilterChange(upsertFilter(activeFilters, filter.id, { ...rangeValue, from: event.target.value }))
                  }
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  aria-label={`${filter.label} from date`}
                />
                <input
                  type="date"
                  value={rangeValue.to ?? ''}
                  onChange={(event) =>
                    onFilterChange(upsertFilter(activeFilters, filter.id, { ...rangeValue, to: event.target.value }))
                  }
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  aria-label={`${filter.label} to date`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
